/**
 * filterPropertyBuilder.ts
 *
 * Shared utility for building filter property options from schemas.
 * Used by TreeMap column filters, StatusFactorsTab, and other filter UIs.
 *
 * This ensures consistent property options across all filter interfaces.
 */

import type { PropertyOption } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';
import { urgencyLevels } from '../constants';
import type { ContextFlowLevel } from '../types/contextFlow';
import type {
  ArcFilter,
  ArcHealthStatus,
  ArcLifecycle,
  PerspectiveFilter,
  RelationshipFilter,
  UnifiedFilters,
} from 'types/filters';
import type { SchemaRelationship } from 'types/schema';

// ── Canonical unconfigured-leaf filters for the lane pseudo-properties ─────────
// A lane leaf (arc / relationship / perspective) is authored as a
// `{property:'<lane>', value}` pseudo-row. EVERY path that seeds one — the
// property picker, the rail add button, and drag-drop — MUST seed it with a
// well-formed filter object, so that serialization always wraps it to its
// canonical `{kind}` form on save. A leaf left with `value: undefined` would
// fall through serialization as a RAW `{property:'arc'|'relationship'|
// 'perspective'}` row, which an engine widens on (matches everything) — the
// never-widen violation SC6 forbids. These are the single source of truth for
// that seed value.
export const DEFAULT_ARC_FILTER: ArcFilter = { operator: 'in', arcSelector: 'any' };
export const DEFAULT_RELATIONSHIP_FILTER: RelationshipFilter = {
  relationshipSystemName: '',
  operator: 'in',
  relatedTo: [],
};
// Seed for the perspective group-leaf's property-picker path (EAINT-725 Phase
// 4a) AND the rail/drag path (Phase 4b, via `defaultLaneFilter('perspective')`
// below).
export const DEFAULT_PERSPECTIVE_FILTER: PerspectiveFilter = {
  perspectiveSystemName: '',
  operator: 'in',
  assignees: [],
};

/** A fresh canonical unconfigured filter for a lane leaf of the given kind. */
export function defaultLaneFilter(kind: 'arc'): ArcFilter;
export function defaultLaneFilter(kind: 'relationship'): RelationshipFilter;
export function defaultLaneFilter(kind: 'perspective'): PerspectiveFilter;
export function defaultLaneFilter(
  kind: 'arc' | 'relationship' | 'perspective'
): ArcFilter | RelationshipFilter | PerspectiveFilter;
export function defaultLaneFilter(
  kind: 'arc' | 'relationship' | 'perspective'
): ArcFilter | RelationshipFilter | PerspectiveFilter {
  if (kind === 'arc') return { ...DEFAULT_ARC_FILTER };
  if (kind === 'relationship') return { ...DEFAULT_RELATIONSHIP_FILTER };
  return { ...DEFAULT_PERSPECTIVE_FILTER };
}

/** Schema type from Redux store */
export interface SchemaForFilters {
  system_name: string;
  name: string;
  is_abstract?: boolean;
  is_hidden?: boolean;
  avatar?: string;
  flow?: string;
  /** For abstract schemas, the concrete system_names that inherit from this label. */
  children?: string[];
  /** Abstract parent labels this schema inherits from (e.g. ["center_sphere"]). */
  parents?: string[];
  properties?: Array<{
    name: string;
    label?: string;
    type?: string;
    hidden?: boolean;
    /** Choice options live here (`{label,value}[]`), matching `SchemaProperty.metadata.options` — real schema properties never carry a bare `.enum`. */
    metadata?: Record<string, any>;
    format?: string;
    /** Catalog identity — see `PropertyOption.aspectIdentity` (EAINT-898/899). */
    aspect_identity?: string;
    /** Present when composed via an aspect; absent for synthetic (inline) properties. */
    aspect_system_name?: string;
    /** Composing aspect's `family` path, copied at merge. */
    aspect_family?: string;
    /** `node` | `edge` | `attribute` — storage location on the merged property. */
    defined_on?: 'node' | 'edge' | 'attribute';
    read_only?: boolean;
    label_key?: string;
  }>;
  /** Perspectives defined at schema level (used by TreeMap and other components) */
  perspectives?: Array<{
    system_name?: string;
    systemName?: string;
    name?: string;
    label?: string;
    is_hidden?: boolean;
    is_system?: boolean;
  }>;
  /** Domain-semantic relationships declared on this schema (additive inheritance — see SchemaRelationship). */
  relationships?: SchemaRelationship[];
  definition?: {
    properties?: Array<{
      key: string;
      label?: string;
      type?: string;
      hidden?: boolean;
      metadata?: Record<string, any>;
      format?: string;
    }>;
    perspectives?: Array<{
      system_name: string;
      label?: string;
      is_hidden?: boolean;
    }>;
  };
}

/** Arc template type for arc stage extraction */
export interface ArcTemplateForFilters {
  definition?: {
    stages?: Array<{
      id?: string;
      stageId?: string;
      fallbackName?: string;
      localizationKey?: string;
      icon?: string;
    }>;
  };
}

/** Options for building property options */
export interface BuildPropertyOptionsConfig {
  /** All schemas from Redux */
  schemas: SchemaForFilters[];

  /** Optional: specific schemas to build properties from (if not provided, uses all schemas) */
  eligibleSchemas?: SchemaForFilters[];

  /** Optional: arc templates for arc stage extraction */
  arcTemplates?: Record<string, { template?: ArcTemplateForFilters }>;

  /** Translation function. Accepts arbitrary interpolation params alongside `defaultValue`. */
  t: (key: string, options?: { defaultValue: string; [param: string]: unknown }) => string;

  /** Whether to include arc-related properties (arc stage, health status) */
  includeArcProperties?: boolean;

  /** Whether to include custom schema properties */
  includeCustomProperties?: boolean;

  /**
   * When 'category', each returned option carries `group`/`groupLabel` for grouped pickers.
   * Off by default — flat output preserves backwards compatibility.
   */
  groupBy?: 'category';

  /**
   * Omit the `type` (Sphere Type) and `label` (Sphere Family) options from the
   * property picker. Callers that surface the dedicated `SphereTypeSelectMolecule`
   * chip selector (the family lane / applicability "Specific types") set this so
   * type/family is authored through that one selector, not a redundant generic
   * enum property.
   */
  omitSphereTypeFamily?: boolean;
}

/**
 * Convert `allow_domain_join` → "Allow domain join". Used when a schema prop omits its label.
 *
 * Exported so display surfaces fall back the SAME way the option builders below
 * do — `FilterChipsDisplayMolecule`'s arc lane resolves health/lifecycle through
 * the identical `t(key, { defaultValue: prettifyKey(value) })` pair
 * `buildFilterArcOptions` uses, so a chip can never disagree with the editor
 * that authored it.
 */
export function prettifyKey(key: string): string {
  if (!key) return key;
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Does a second schema's declaration of an already-emitted property key differ
 * in shape? A different data type, or a different set of enum values, means the
 * single emitted option's value editor cannot be right for both owners.
 */
function declarationDiffers(
  existing: PropertyOption,
  type: PropertyOption['type'],
  enumValues: PropertyOption['enumValues']
): boolean {
  if (existing.type !== type) return true;
  const a = (existing.enumValues ?? []).map((v) => v.value).sort((x, y) => x.localeCompare(y));
  const b = (enumValues ?? []).map((v) => v.value).sort((x, y) => x.localeCompare(y));
  return a.length !== b.length || a.some((v, i) => v !== b[i]);
}

/** Built-in group taxonomy for the picker UI. */
const GROUP_IDENTITY = 'identity';
const GROUP_STATUS = 'status';
const GROUP_METRICS = 'metrics';
const GROUP_DATES = 'dates';
const GROUP_ARC = 'arc';
const GROUP_RELATIONSHIP = 'relationship';
const GROUP_PERSPECTIVE = 'perspective';
const GROUP_SCHEMA_PREFIX = 'schema:';

/**
 * An aspect's `family` (EAINT-899 — `basePlatformAspectDefinitions.js` et al.) already
 * uses the same four names as the picker's built-in taxonomy buckets
 * (`identity`/`status`/`metrics`/`dates`), so a composed property groups by its
 * OWN aspect family rather than by the schema that happens to compose it. Only a
 * synthetic (non-aspect) property falls back to `schema:<system_name>`.
 */
const ASPECT_FAMILY_GROUP: Record<string, string> = {
  identity: GROUP_IDENTITY,
  status: GROUP_STATUS,
  metrics: GROUP_METRICS,
  dates: GROUP_DATES,
};

/**
 * Build property options for filter UI from schemas
 *
 * This creates a consistent set of PropertyOption[] including:
 * - Type (enum of all non-abstract schema types)
 * - Label (enum of all abstract schema types)
 * - Urgency (urgency type with urgency levels)
 * - Arc Stage (if arc templates provided)
 * - Health Status (if schemas have arcs)
 * - Standard sphere properties (name, description, dates)
 * - Custom schema properties
 */
export function buildFilterPropertyOptions(config: BuildPropertyOptionsConfig): PropertyOption[] {
  const {
    schemas,
    eligibleSchemas = schemas,
    arcTemplates,
    t,
    includeArcProperties = true,
    includeCustomProperties = true,
    groupBy,
    omitSphereTypeFamily = false,
  } = config;

  const propertiesMap = new Map<string, PropertyOption>();
  const grouped = groupBy === 'category';

  // Group-label resolver. Pulled to a local so we can centralize the i18n keys.
  const groupLabelFor = (group: string): string | undefined => {
    if (!grouped) return undefined;
    if (group === GROUP_IDENTITY) return t('filters.groups.identity', { defaultValue: 'Identity' });
    if (group === GROUP_STATUS) return t('filters.groups.status', { defaultValue: 'Status & Health' });
    if (group === GROUP_METRICS) return t('filters.groups.metrics', { defaultValue: 'Metrics' });
    if (group === GROUP_DATES) return t('filters.groups.dates', { defaultValue: 'Dates' });
    if (group === GROUP_ARC) return t('filters.groups.arc', { defaultValue: 'Arcs' });
    if (group === GROUP_RELATIONSHIP) return t('filters.groups.relationship', { defaultValue: 'Relationships' });
    if (group === GROUP_PERSPECTIVE) return t('filters.groups.perspective');
    return undefined; // schema:* labels are resolved at the call site (schema name varies)
  };

  // === Special property: Type (all non-abstract schema types) ===
  const allSchemaTypes = schemas
    .filter((s) => !s.is_abstract && !s.is_hidden)
    .map((s) => ({
      label: s.name,
      value: s.system_name,
      avatar: s.avatar,
    }));

  // Sphere Type / Sphere Family are authored via the SphereTypeSelect chip
  // selector (the family lane), so callers with that selector omit them here to
  // avoid a redundant second surface writing the same `type`/`label` filters.
  if (!omitSphereTypeFamily) {
    propertiesMap.set('type', {
      key: 'type',
      label: t('filters.properties.sphereType', { defaultValue: 'Sphere Type' }),
      type: 'enum',
      enumValues: [...allSchemaTypes].sort((a, b) => a.label.localeCompare(b.label)),
      group: grouped ? GROUP_IDENTITY : undefined,
      groupLabel: groupLabelFor(GROUP_IDENTITY),
      definedOn: 'node',
    });
  }

  // === Special property: Label (all abstract schema types / sphere families) ===
  const abstractSchemaTypes = schemas
    .filter((s) => s.is_abstract && !s.is_hidden)
    .map((s) => ({
      label: s.name,
      value: s.system_name,
      avatar: s.avatar,
    }));

  if (!omitSphereTypeFamily && abstractSchemaTypes.length > 0) {
    propertiesMap.set('label', {
      key: 'label',
      label: t('filters.properties.sphereFamily', { defaultValue: 'Sphere Family' }),
      type: 'enum',
      enumValues: [...abstractSchemaTypes].sort((a, b) => a.label.localeCompare(b.label)),
      group: grouped ? GROUP_IDENTITY : undefined,
      groupLabel: groupLabelFor(GROUP_IDENTITY),
      definedOn: 'node',
    });
  }

  // === Urgency property with urgency levels ===
  propertiesMap.set('urgency', {
    key: 'urgency',
    label: t('filters.properties.urgency', { defaultValue: 'Urgency' }),
    type: 'urgency',
    enumValues: urgencyLevels.map((level) => ({
      label: level.label,
      value: level.value.toString(),
    })),
    group: grouped ? GROUP_STATUS : undefined,
    groupLabel: groupLabelFor(GROUP_STATUS),
    definedOn: 'edge',
  });

  // === Universal platform metric (SC2) ===
  // gravity is a real computed value present on every sphere (`center_gravity`,
  // edge-stored on ASSIGNED_TO — EAINT-899 basePlatformAspectDefinitions.js).
  // Always emitted, regardless of `eligibleSchemas` scoping. `velocity`/`impact`
  // were never-backed phantoms (no aspect, no vertex/edge property) and are
  // deleted — see `catalogEmitsNoPhantomProperties` (SC1).
  propertiesMap.set('gravity', {
    key: 'gravity',
    label: t('filters.properties.gravity', { defaultValue: 'Gravity' }),
    type: 'numeric',
    group: grouped ? GROUP_METRICS : undefined,
    groupLabel: groupLabelFor(GROUP_METRICS),
    definedOn: 'edge',
  });
  // Deliberately no flat 'status' property here: status/stage/health/lifecycle
  // live on ARCS, not the sphere (DESIGN_NOTES.md#filter-lanes). A flat status
  // enum is structurally wrong — it cannot express which arc it describes.
  // Arc-as-a-property-type (SC2) is how this is authored instead: `Add filter →
  // Arc` (see PropertyType 'arc' below).

  // === Arc-related properties ===
  if (includeArcProperties) {
    // Arc Stage property - collect all unique arc stages from eligible schemas
    const arcStageMap = new Map<string, { label: string; value: string; icon?: string }>();

    eligibleSchemas.forEach((schema) => {
      const arcSystemName = schema.flow;
      if (!arcSystemName || !arcTemplates) return;

      // Find the arc template
      const arcTemplateEntry = arcTemplates[arcSystemName];
      const arcTemplate = arcTemplateEntry?.template;

      if (arcTemplate?.definition?.stages) {
        arcTemplate.definition.stages.forEach((stage) => {
          const stageId = stage.id || stage.stageId;
          if (!stageId) return;

          // Use fallbackName (the display caption), or fall back to id
          const stageLabel = stage.fallbackName || stageId;

          if (!arcStageMap.has(stageId)) {
            arcStageMap.set(stageId, {
              label: stageLabel,
              value: stageId,
              icon: stage.icon,
            });
          }
        });
      }
    });

    if (arcStageMap.size > 0) {
      propertiesMap.set('currentStageId', {
        key: 'currentStageId',
        label: t('filters.properties.currentStageId', { defaultValue: 'Arc Stage' }),
        type: 'enum',
        enumValues: Array.from(arcStageMap.values()),
        group: grouped ? GROUP_STATUS : undefined,
        groupLabel: groupLabelFor(GROUP_STATUS),
      });
    }

    // Health Status property - available for arc-enabled spheres
    const hasArcSchemas = eligibleSchemas.some((schema) => schema.flow);
    if (hasArcSchemas) {
      propertiesMap.set('healthStatus', {
        key: 'healthStatus',
        label: t('filters.properties.healthStatus', { defaultValue: 'Health Status' }),
        type: 'enum',
        enumValues: [
          { label: t('arc.health.on_track', { defaultValue: 'On Track' }), value: 'on_track' },
          { label: t('arc.health.at_risk', { defaultValue: 'At Risk' }), value: 'at_risk' },
          { label: t('arc.health.overdue', { defaultValue: 'Overdue' }), value: 'overdue' },
          { label: t('arc.health.blocked', { defaultValue: 'Blocked' }), value: 'blocked' },
          {
            label: t('arc.health.attention_needed', { defaultValue: 'Attention Needed' }),
            value: 'attention_needed',
          },
        ],
        group: grouped ? GROUP_STATUS : undefined,
        groupLabel: groupLabelFor(GROUP_STATUS),
      });

      // "Arc" pseudo-property (SC2/EF_ARC_PROP) — a correlated multi-axis filter
      // (selector + health/stage/lifecycle, all matching the SAME arc), distinct
      // from the flat healthStatus/currentStageId properties above. Its own
      // group keeps it visually separate in the picker (DESIGN_NOTES.md#filter-lanes).
      propertiesMap.set('arc', {
        key: 'arc',
        label: t('filters.properties.arc', { defaultValue: 'Arc' }),
        type: 'arc',
        group: grouped ? GROUP_ARC : undefined,
        groupLabel: groupLabelFor(GROUP_ARC),
      });
    }
  }

  // === Relationship pseudo-property (EAINT-725 Phase 2, sibling to 'arc' above) ===
  // A correlated single-relationship condition (which relationship + direction +
  // related spheres) — distinct from offering a per-relationship-name property,
  // the same way 'arc' offers one pseudo-property rather than one per template.
  // Only offered when at least one eligible schema declares a relationship.
  const hasRelationships = eligibleSchemas.some((schema) => (schema.relationships?.length ?? 0) > 0);
  if (hasRelationships) {
    propertiesMap.set('relationship', {
      key: 'relationship',
      label: t('filters.properties.relationship', { defaultValue: 'Relationship' }),
      type: 'relationship',
      group: grouped ? GROUP_RELATIONSHIP : undefined,
      groupLabel: groupLabelFor(GROUP_RELATIONSHIP),
    });
  }

  // === Perspective pseudo-property (EAINT-725 Phase 4a, sibling to 'arc'/'relationship') ===
  // A correlated single-perspective condition (which perspective + has/lacks +
  // assignee narrowing) — distinct from offering a per-perspective-name
  // property, the same way 'arc'/'relationship' offer one pseudo-property
  // rather than one per template/relationship name. Unlike arc/relationship,
  // this is NOT gated on a schema declaring anything: every sphere inherits
  // the baseline perspectives (center_parent/center_responsible/center_center
  // — see `buildFilterPerspectiveOptions`), so the pseudo-property is always offered.
  propertiesMap.set('perspective', {
    key: 'perspective',
    label: t('filters.properties.perspective'),
    type: 'perspective',
    group: grouped ? GROUP_PERSPECTIVE : undefined,
    groupLabel: groupLabelFor(GROUP_PERSPECTIVE),
  });

  // === Standard sphere properties ===
  propertiesMap.set('name', {
    key: 'name',
    label: t('filters.properties.name', { defaultValue: 'Name' }),
    type: 'text',
    group: grouped ? GROUP_IDENTITY : undefined,
    groupLabel: groupLabelFor(GROUP_IDENTITY),
    definedOn: 'node',
  });
  propertiesMap.set('description', {
    key: 'description',
    label: t('filters.properties.description', { defaultValue: 'Description' }),
    type: 'text',
    group: grouped ? GROUP_IDENTITY : undefined,
    groupLabel: groupLabelFor(GROUP_IDENTITY),
    definedOn: 'node',
  });
  // `created_at`/`updated_at` were never-backed phantoms under those snake_case
  // keys. The real values are the `createdAt`/`updatedAt` composed properties
  // (`center_created_at`/`center_updated_at`, EAINT-899) that reach the catalog
  // through every schema's merged `properties` below (see `catalogEmitsNoPhantomProperties`).

  // === Custom schema properties ===
  // In strict mode the schema list is the in-scope set explicitly (no leakage from other schemas).
  // In permissive mode (legacy callers) we use eligibleSchemas which defaults to all schemas.
  if (includeCustomProperties) {
    eligibleSchemas.forEach((schema) => {
      // Check both schema.properties and schema.definition.properties
      const properties = schema.properties || schema.definition?.properties || [];
      const schemaGroup = `${GROUP_SCHEMA_PREFIX}${schema.system_name}`;
      const schemaGroupLabel = grouped
        ? t('filters.groups.schemaProps', {
            schemaName: schema.name,
            defaultValue: `${schema.name} properties`,
          })
        : undefined;
      // i18n fallback path: most translations don't know the schema name, so the
      // `defaultValue` above already includes it. We still resolve the key in case
      // a translator overrides it.

      properties.forEach((prop: any) => {
        // Skip hidden properties
        if (prop.hidden) return;

        const propertyKey = prop.name || prop.key;
        if (!propertyKey) return;

        let type: PropertyOption['type'] = 'text';
        let enumValues: PropertyOption['enumValues'];
        let referenceTargetSchema: string | undefined;

        // Choice options live in `metadata.options` (`{label,value}[]`) — real
        // `SchemaProperty`s never carry a bare `.enum` array (types/schema.d.ts).
        const choiceOptions = prop.metadata?.options as
          | Array<{ label: string; value: string }>
          | undefined;

        // Map schema property types to filter types
        if (prop.type === 'string' && Array.isArray(choiceOptions) && choiceOptions.length > 0) {
          type = 'enum';
          enumValues = choiceOptions.map((o) => ({ label: o.label, value: o.value }));
        } else if (prop.type === 'number' || prop.type === 'integer') {
          type = 'numeric';
        } else if (prop.type === 'boolean') {
          type = 'boolean';
        } else if (prop.type === 'datetime' || prop.format === 'date-time') {
          type = 'datetime';
        } else if (prop.type === 'date' || prop.format === 'date') {
          type = 'date';
        } else if (prop.type === 'reference') {
          type = 'reference';
          // The reference's declared target type scopes the sphere picker its
          // `in`/`not_in` value editor opens (`SphereMultiSelectValueMolecule`).
          // Without it the picker cannot tell which spheres the property could
          // hold, so it must travel with the catalog entry.
          referenceTargetSchema = prop.metadata?.targetSchemaSystemName;
        }

        // A key declared by several schemas is still emitted ONCE per
        // `aspect_identity` (or, for a synthetic/inline property with no
        // `aspect_identity`, once per property name) — two schemas composing the
        // SAME aspect merge into one option with accumulated `ownerTypes`
        // (`catalogEmitsOneOptionPerAspectIdentity`, SC1). Ownership accumulates
        // rather than being discarded — scope-aware pickers need to know every
        // type that declares the key, and a shape mismatch between owners is a
        // real signal, not noise to swallow.
        const existing = propertiesMap.get(propertyKey);
        if (existing) {
          // A universal property (no `ownerTypes`) shadowed by a same-named
          // schema property stays universal — it is on every sphere regardless.
          if (existing.ownerTypes) {
            if (!existing.ownerTypes.includes(schema.system_name)) {
              existing.ownerTypes.push(schema.system_name);
            }
            if (!existing.hasVariants && declarationDiffers(existing, type, enumValues)) {
              existing.hasVariants = true;
            }
          }
          if (!existing.aspectIdentity && prop.aspect_identity) {
            existing.aspectIdentity = prop.aspect_identity;
            existing.aspectSystemName = prop.aspect_system_name;
            existing.aspectFamily = prop.aspect_family;
            existing.definedOn = prop.defined_on;
          }
          return;
        }

        // A composed property groups by its OWN aspect family (identity / status /
        // metrics / dates), matching the picker's built-in taxonomy — see
        // `ASPECT_FAMILY_GROUP`. A synthetic (non-aspect) property falls back to
        // "<Schema> properties", with datetime/date props still promoted to Dates.
        const isDateLike = type === 'datetime' || type === 'date';
        const familyGroup = prop.aspect_family ? ASPECT_FAMILY_GROUP[prop.aspect_family] : undefined;
        const customGroup = familyGroup ?? (isDateLike ? GROUP_DATES : schemaGroup);
        // Both non-schema branches label whatever `customGroup` resolved to; only the schema
        // fallback carries its own label. Derived from `customGroup` rather than re-deriving the
        // same two conditions (Sonar S3358), which also makes it impossible for a group and its
        // label to disagree — the failure the duplicated conditions invited.
        const customGroupLabel =
          familyGroup === undefined && !isDateLike ? schemaGroupLabel : groupLabelFor(customGroup);

        // `label_key` (EAINT-899) resolves through `t()`, falling back to the
        // merged property's own `label`/prettified name — same fallback chain
        // every other option in this builder uses (`catalogHonoursLabelKey`, SC1).
        const label = prop.label_key
          ? t(prop.label_key, { defaultValue: prop.label || prettifyKey(prop.name || prop.key) })
          : prop.label || prettifyKey(prop.name || prop.key);

        propertiesMap.set(propertyKey, {
          key: propertyKey,
          label,
          type,
          enumValues,
          referenceTargetSchema,
          isCustom: true,
          ownerTypes: [schema.system_name],
          group: grouped ? customGroup : undefined,
          groupLabel: customGroupLabel,
          aspectIdentity: prop.aspect_identity,
          aspectSystemName: prop.aspect_system_name,
          aspectFamily: prop.aspect_family,
          definedOn: prop.defined_on,
        });
      });
    });
  }

  // Sort all properties by label for consistent ordering. Group order is determined
  // at the picker layer (callers can sort by `group` first via the conventional order).
  return Array.from(propertiesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/** Stable category order for grouped pickers — pinned at the call site. */
export const PROPERTY_GROUP_ORDER: readonly string[] = [
  GROUP_IDENTITY,
  GROUP_STATUS,
  GROUP_METRICS,
  GROUP_DATES,
  // schema:* groups slot in here, sorted by groupLabel
];

/**
 * Order a flat PropertyOption list into groups. Returns an array of group buckets
 * with their entries sorted by label. Schema-specific groups appear after the
 * fixed taxonomy (identity → status → dates → schemas…).
 */
export function groupPropertyOptions(
  options: PropertyOption[]
): Array<{ group: string; label: string; items: PropertyOption[] }> {
  const buckets = new Map<string, { group: string; label: string; items: PropertyOption[] }>();
  for (const opt of options) {
    const g = opt.group || 'other';
    const label = opt.groupLabel || g;
    if (!buckets.has(g)) buckets.set(g, { group: g, label, items: [] });
    buckets.get(g)!.items.push(opt);
  }
  const ordered: Array<{ group: string; label: string; items: PropertyOption[] }> = [];
  for (const g of PROPERTY_GROUP_ORDER) {
    if (buckets.has(g)) {
      ordered.push(buckets.get(g)!);
      buckets.delete(g);
    }
  }
  // Remaining (schema:* and 'other') sorted by label.
  const remaining = Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
  ordered.push(...remaining);
  // Sort items inside each bucket alphabetically by label.
  for (const bucket of ordered) {
    bucket.items.sort((a, b) => a.label.localeCompare(b.label));
  }
  return ordered;
}

/**
 * Given the property filters of a level (or segment), figure out the in-scope
 * schemas for the picker.
 *
 * Rules:
 *  - Read `type` (concrete) or `label` (family) filters with operator `in` or `equals`.
 *    Collect their values. A `label` value names an abstract schema — its concrete
 *    children are expanded exactly like an abstract `type` value.
 *  - Negative operators (`not_in`, `not_equals`) → ignored for scoping.
 *  - If any value matches an abstract schema, expand it to its concrete children.
 *  - If no positive `type`/`label` filter exists → return [] (caller treats as un-scoped).
 *
 * @param filters - propertyFilters on the level/segment (FilterExpression[])
 * @param allSchemas - the full schema list (used to resolve system_name → schema and to expand abstracts)
 * @returns the schemas this level is type-scoped to, or [] if un-scoped
 */
/** Structural leaf shape both group models agree on. */
type ScopeLeaf = { property?: string; operator?: string; value?: any; values?: any };

/**
 * Structural union of the two group models in play: the editor's
 * `SimpleFilterGroup` (`filters`) and the canonical `FilterGroup`
 * (`expressions`). Both nest through `groups`.
 *
 * Members are typed loosely on purpose — the canonical `FilterGroupLeaf` is a
 * union whose lane members carry none of `ScopeLeaf`'s keys, so a precise
 * element type would make every real caller fail TS's weak-type check. Only
 * `property`/`operator`/`value`/`values` are ever read, and each read is
 * type-guarded.
 */
type ScopeGroup = {
  filters?: readonly any[];
  expressions?: readonly any[];
  groups?: readonly ScopeGroup[];
};

/** A filter carrying leaves — `FilterState`, `UnifiedFilters`, or anything shaped like them. */
type ScopeSource = { propertyFilters?: readonly any[]; filterGroups?: readonly any[] };

/**
 * Flatten every leaf of a filter — top-level `propertyFilters` plus the whole
 * recursive group tree, in either group model — into one list.
 *
 * OR branches are unioned rather than intersected. That is deliberate and safe
 * *for catalog scoping only*: the result feeds which properties a picker
 * offers, so a superset merely offers a few extra properties. It must never be
 * used to narrow filter execution, where unioning an OR would widen the result
 * set (never-widen).
 */
export function collectFilterScopeLeaves(source: ScopeSource | undefined): ScopeLeaf[] {
  if (!source) return [];
  const out: ScopeLeaf[] = [...(source.propertyFilters ?? [])];
  const walk = (groups: readonly ScopeGroup[] | undefined) => {
    (groups ?? []).forEach((g) => {
      out.push(...(g.filters ?? []), ...(g.expressions ?? []));
      walk(g.groups);
    });
  };
  walk(source.filterGroups as readonly ScopeGroup[] | undefined);
  return out;
}

export function inferEligibleSchemas(
  filters: Array<{ property?: string; operator?: string; value?: any; values?: any }> | undefined,
  allSchemas: SchemaForFilters[]
): SchemaForFilters[] {
  if (!filters || filters.length === 0) return [];

  // Collect positive type/label-filter values.
  const targetSystemNames = new Set<string>();
  for (const f of filters) {
    if (f.property !== 'type' && f.property !== 'label') continue;
    if (f.operator !== 'in' && f.operator !== 'equals') continue;
    // 'equals' uses .value (single); 'in' may use .value (array) or legacy .values
    const v = f.values && Array.isArray(f.values) ? f.values : f.value;
    if (Array.isArray(v)) {
      v.forEach((x) => typeof x === 'string' && x && targetSystemNames.add(x));
    } else if (typeof v === 'string' && v) {
      targetSystemNames.add(v);
    }
  }

  if (targetSystemNames.size === 0) return [];

  // Resolve names to schemas. Abstract → expand to concrete children.
  const byName = new Map<string, SchemaForFilters>(allSchemas.map((s) => [s.system_name, s]));
  const resolved = new Map<string, SchemaForFilters>();

  const addConcrete = (s: SchemaForFilters) => {
    if (!s.is_abstract && !s.is_hidden) resolved.set(s.system_name, s);
  };
  const expandAbstract = (s: SchemaForFilters) => {
    // Prefer explicit children if the schema model carries them.
    if (s.children && s.children.length > 0) {
      s.children.forEach((childName) => {
        const child = byName.get(childName);
        if (child) {
          if (child.is_abstract) {
            expandAbstract(child);
          } else {
            addConcrete(child);
          }
        }
      });
      return;
    }
    // Fallback: find any schema that claims this abstract as a parent.
    allSchemas
      .filter((other) => other.parents?.includes(s.system_name))
      .forEach((child) => (child.is_abstract ? expandAbstract(child) : addConcrete(child)));
  };

  for (const name of targetSystemNames) {
    const s = byName.get(name);
    if (!s) continue;
    if (s.is_abstract) expandAbstract(s);
    else addConcrete(s);
  }

  return Array.from(resolved.values());
}

/**
 * Build perspective options for filter UI from schemas
 *
 * This creates perspective options based on schema definitions.
 */
export interface PerspectiveOptionForFilters {
  systemName: string;
  label: string;
  icon?: string;
}

export interface BuildPerspectiveOptionsConfig {
  /** Schemas to extract perspectives from */
  schemas: SchemaForFilters[];

  /** Translation function */
  t: (key: string, options?: { defaultValue: string }) => string;

  /** Whether to include default Center perspectives */
  includeDefaultPerspectives?: boolean;
}

/**
 * Build perspective options for filter UI
 */
export function buildFilterPerspectiveOptions(
  config: BuildPerspectiveOptionsConfig
): PerspectiveOptionForFilters[] {
  const { schemas, t, includeDefaultPerspectives = true } = config;

  const perspectivesMap = new Map<string, PerspectiveOptionForFilters>();

  // Add default Center perspectives
  if (includeDefaultPerspectives) {
    perspectivesMap.set('center_parent', {
      systemName: 'center_parent',
      label: t('filters.perspectives.centerParent', { defaultValue: 'Parent (Children)' }),
      icon: 'pi pi-sitemap',
    });
    perspectivesMap.set('center_responsible', {
      systemName: 'center_responsible',
      label: t('filters.perspectives.centerResponsible', { defaultValue: 'Responsible' }),
      icon: 'pi pi-user',
    });
    perspectivesMap.set('center_center', {
      systemName: 'center_center',
      label: t('filters.perspectives.centerCenter', { defaultValue: 'Center' }),
      icon: 'pi pi-building',
    });
  }

  // Extract perspectives from schemas
  // Check both schema.perspectives (top-level, used by TreeMap) and schema.definition.perspectives
  // Filter out hidden perspectives (is_hidden === true)
  schemas.forEach((schema) => {
    // First check top-level perspectives (primary location used by TreeMap)
    if (schema.perspectives && Array.isArray(schema.perspectives)) {
      schema.perspectives.forEach((perspective) => {
        const systemName = perspective.system_name || perspective.systemName;
        if (!systemName) return;
        if (perspectivesMap.has(systemName)) return;
        // Skip hidden perspectives
        if (perspective.is_hidden) return;

        perspectivesMap.set(systemName, {
          systemName,
          label: perspective.name || perspective.label || systemName,
        });
      });
    }

    // Also check definition.perspectives for backwards compatibility
    const defPerspectives = schema.definition?.perspectives || [];
    defPerspectives.forEach((perspective) => {
      if (!perspective.system_name) return;
      if (perspectivesMap.has(perspective.system_name)) return;
      // Skip hidden perspectives
      if (perspective.is_hidden) return;

      perspectivesMap.set(perspective.system_name, {
        systemName: perspective.system_name,
        label: perspective.label || perspective.system_name,
      });
    });
  });

  return Array.from(perspectivesMap.values());
}

/** One perspective offered by `buildPerspectiveCatalog`, grouped under its owning type. */
export interface PerspectiveCatalogEntry {
  systemName: string;
  label: string;
  icon?: string;
}

/** One group in the picker — the baseline set, or everything one schema declares. */
export interface PerspectiveCatalogGroup {
  /** `'common'` for the baseline group; the owning schema's `system_name` otherwise. */
  id: string;
  label: string;
  note?: string;
  /**
   * False when `scopedTypeSystemNames` is non-empty and doesn't include this group's
   * owning type — the perspective stays selectable (a column can still bind it), the
   * caller renders an OUT OF SCOPE badge and a warning that every cell will be empty.
   */
  inScope: boolean;
  perspectives: PerspectiveCatalogEntry[];
}

export interface BuildPerspectiveCatalogConfig {
  /** Every schema reachable in the org — used to discover per-type perspectives. */
  schemas: SchemaForFilters[];
  /**
   * `system_name`s of the sphere types this context view actually walks. A group whose
   * owning type isn't in this set is flagged `inScope: false`. Empty means "unknown" —
   * treat everything as in scope rather than flagging groups without cause.
   */
  scopedTypeSystemNames: string[];
  t: (key: string, options?: { typeName?: string }) => string;
  /** Whether to include the built-in Center perspectives (Parent, Responsible, Center). */
  includeDefaultPerspectives?: boolean;
}

/**
 * Build the grouped perspective catalog for `PerspectivePickerMolecule` — "Common"
 * (built-in, inherited by every type) plus one group per schema that declares its own
 * perspectives.
 *
 * NOTE: unlike the wireframe this implements (`PF_GROUPS`/`PF_META` in the View
 * Configuration handoff), groups here carry no `src: 'org' | 'approach'` distinction.
 * Schema perspective data (`SchemaForFilters.perspectives`) has no field recording
 * whether a deployed approach added a perspective versus a human authoring the schema
 * directly — `is_system` marks perspectives the platform locks against assign/unassign,
 * not provenance. Fabricating that split would show authors a distinction the data
 * can't back up; see spec Open Questions for a suggested backend follow-up.
 */
export function buildPerspectiveCatalog(config: BuildPerspectiveCatalogConfig): PerspectiveCatalogGroup[] {
  const { schemas, scopedTypeSystemNames, t, includeDefaultPerspectives = true } = config;
  const scopedSet = new Set(scopedTypeSystemNames);
  const isInScope = (typeSystemName: string) => scopedSet.size === 0 || scopedSet.has(typeSystemName);

  const groups: PerspectiveCatalogGroup[] = [];
  // Only the Common group claims system names globally — a perspective declared on two
  // sphere types belongs to BOTH type groups. Deduping across schemas would attribute it
  // to whichever schema iterates first, and badge it OUT OF SCOPE when that arbitrary
  // winner happens to be a type the view doesn't walk.
  const commonSystemNames = new Set<string>();

  if (includeDefaultPerspectives) {
    const defaults: PerspectiveCatalogEntry[] = [
      { systemName: 'center_parent', label: t('context.viewEditor.atlas.perspectivePicker.centerParent'), icon: 'pi pi-sitemap' },
      { systemName: 'center_responsible', label: t('context.viewEditor.atlas.perspectivePicker.centerResponsible'), icon: 'pi pi-user' },
      { systemName: 'center_center', label: t('context.viewEditor.atlas.perspectivePicker.centerCenter'), icon: 'pi pi-building' },
    ];
    defaults.forEach((p) => commonSystemNames.add(p.systemName));
    groups.push({
      id: 'common',
      label: t('context.viewEditor.atlas.perspectivePicker.commonGroup'),
      note: t('context.viewEditor.atlas.perspectivePicker.commonGroupNote'),
      inScope: true,
      perspectives: defaults,
    });
  }

  schemas.forEach((schema) => {
    const declared = new Map<string, string>();

    (schema.perspectives ?? []).forEach((p) => {
      const systemName = p.system_name || p.systemName;
      if (!systemName || p.is_hidden || commonSystemNames.has(systemName) || declared.has(systemName)) return;
      declared.set(systemName, p.name || p.label || systemName);
    });
    (schema.definition?.perspectives ?? []).forEach((p) => {
      if (!p.system_name || p.is_hidden || commonSystemNames.has(p.system_name) || declared.has(p.system_name)) return;
      declared.set(p.system_name, p.label || p.system_name);
    });

    if (declared.size === 0) return;

    groups.push({
      id: schema.system_name,
      label: t('context.viewEditor.atlas.perspectivePicker.onTypeGroup', { typeName: schema.name }),
      note: t('context.viewEditor.atlas.perspectivePicker.onTypeGroupNote'),
      inScope: isInScope(schema.system_name),
      perspectives: Array.from(declared.entries()).map(([systemName, label]) => ({ systemName, label })),
    });
  });

  return groups;
}

/**
 * Collects the `system_name`s a context's levels constrain their spheres to,
 * by reading each level's top-level `filters.propertyFilters` row for
 * `property === 'type'` — the same pattern `UnifiedFilterEditor`'s
 * `readFamilyTypeValues` uses to read a type filter's bound values. A level
 * with no `type` row (or no filters at all) contributes nothing — it doesn't
 * widen scope, since scope-empty already means "unknown" to the callers of
 * `buildPerspectiveCatalog`/`aspectScopeTypeSystemNames`.
 *
 * Feeds `scopedTypeSystemNames`/`aspectScopeTypeSystemNames` — "the sphere
 * types this context view actually walks" — for the Atlas view editor, whose
 * levels are loaded once up front (not per-column like TreeMap's
 * `getEligibleSchemasForColumn`, which composes runtime filter layers this
 * editor has no equivalent of).
 */
export function collectLevelTypeSystemNames(levels: { filters?: UnifiedFilters }[]): string[] {
  const typeSystemNames = new Set<string>();
  levels.forEach((level) => {
    const typeFilter = (level.filters?.propertyFilters ?? []).find((f) => f.property === 'type');
    if (!typeFilter) return;
    if (Array.isArray(typeFilter.values)) {
      typeFilter.values.forEach((value) => typeof value === 'string' && typeSystemNames.add(value));
    } else if (Array.isArray(typeFilter.value)) {
      typeFilter.value.forEach((value) => typeof value === 'string' && typeSystemNames.add(value));
    } else if (typeof typeFilter.value === 'string' && typeFilter.value) {
      typeSystemNames.add(typeFilter.value);
    }
  });
  return Array.from(typeSystemNames);
}

/**
 * Relationship option for filter UI (RelationshipFilterMolecule's picker list).
 */
export interface RelationshipOptionForFilters {
  systemName: string;
  label: string;
  description?: string;
  relationshipType?: SchemaRelationship['relationship_type'];
  cardinality?: SchemaRelationship['cardinality'];
  /** Scopes the related-sphere picker — a discovery hint, not enforcement (see relationship-filter-ui-brief.md §2). */
  targetFilter?: UnifiedFilters;
}

export interface BuildRelationshipOptionsConfig {
  /** Schemas to extract relationships from */
  schemas: SchemaForFilters[];
  /** Translation function */
  t: (key: string, options?: { defaultValue: string; [param: string]: unknown }) => string;
}

/**
 * Build relationship options for filter UI from schemas.
 *
 * Sibling to `buildFilterPerspectiveOptions` — same unscoped, union-across-`schemas`
 * shape (inheritance is additive, so there is no override semantics to reconcile,
 * unlike perspectives). Deduped by `system_name`; the first schema to declare a
 * given relationship wins (later declarations of the same system_name are
 * inherited copies, not distinct relationships).
 */
export function buildFilterRelationshipOptions(
  config: BuildRelationshipOptionsConfig
): RelationshipOptionForFilters[] {
  const { schemas, t } = config;
  const relationshipsMap = new Map<string, RelationshipOptionForFilters>();

  schemas.forEach((schema) => {
    (schema.relationships ?? []).forEach((rel) => {
      if (!rel.system_name) return;
      if (relationshipsMap.has(rel.system_name)) return;

      relationshipsMap.set(rel.system_name, {
        systemName: rel.system_name,
        label: rel.label_key ? t(rel.label_key, { defaultValue: rel.label }) : rel.label,
        description: rel.description_key
          ? t(rel.description_key, { defaultValue: rel.description ?? '' })
          : rel.description,
        relationshipType: rel.relationship_type,
        cardinality: rel.cardinality,
        targetFilter: rel.target_filter,
      });
    });
  });

  return Array.from(relationshipsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The closed, ordered health/lifecycle value sets an arc filter may author.
 *
 * These mirror the `ArcHealthStatus` / `ArcLifecycle` unions in `types/filters.d.ts`
 * and are declared here (rather than derived) so the option order is a product
 * decision — best-to-worst for health, chronological for lifecycle — not the
 * accident of a type's declaration order.
 *
 * `critical` is deliberately absent from health: it is a dead value in the wider
 * TS union, never persisted or localized (arc-filter-ui-brief.md §4). Offering it
 * would let a user author a filter that can never match.
 */
const ARC_HEALTH_VALUES: readonly ArcHealthStatus[] = [
  'on_track',
  'at_risk',
  'overdue',
  'blocked',
  'attention_needed',
];

const ARC_LIFECYCLE_VALUES: readonly ArcLifecycle[] = [
  'not_started',
  'active',
  'completed',
  'cancelled',
  'error',
  'failed',
];

/** One selectable value in an arc predicate axis. */
export interface ArcFilterOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export interface BuildArcOptionsConfig {
  /** Translation function */
  t: (key: string, options?: { defaultValue: string; [param: string]: unknown }) => string;
  /**
   * The arc template the row is scoped to, if any. Stage options are only
   * meaningful once a template is chosen — `stageId` values are opaque,
   * template-and-version-specific `StageDefinition.id`s, so a flat stage list
   * across templates would be ambiguous (arc-filter-ui-brief.md §5).
   */
  template?: ArcTemplateForFilters;
}

/**
 * Build the option lists for the arc lane's three predicate axes.
 *
 * Sibling to `buildFilterRelationshipOptions` / `buildFilterPerspectiveOptions`.
 * Health and lifecycle are closed localized enums (schema-independent, so no
 * `schemas` input); stages are resolved from the chosen template's
 * `definition.stages` — id stored, name rendered, never the raw id.
 */
export function buildFilterArcOptions(config: BuildArcOptionsConfig): {
  healthOptions: ArcFilterOption<ArcHealthStatus>[];
  lifecycleOptions: ArcFilterOption<ArcLifecycle>[];
  stageOptions: ArcFilterOption[];
} {
  const { t, template } = config;

  const healthOptions = ARC_HEALTH_VALUES.map((value) => ({
    value,
    label: t(`arc.health.${value}`, { defaultValue: prettifyKey(value) }),
  }));

  const lifecycleOptions = ARC_LIFECYCLE_VALUES.map((value) => ({
    value,
    label: t(`arc.lifecycle.${value}`, { defaultValue: prettifyKey(value) }),
  }));

  const stageOptions = (template?.definition?.stages ?? []).reduce<ArcFilterOption[]>(
    (acc, stage) => {
      const id = stage.id ?? stage.stageId;
      if (!id) return acc;
      acc.push({
        value: id,
        label: stage.localizationKey
          ? t(stage.localizationKey, { defaultValue: stage.fallbackName ?? prettifyKey(id) })
          : stage.fallbackName ?? prettifyKey(id),
      });
      return acc;
    },
    []
  );

  return { healthOptions, lifecycleOptions, stageOptions };
}

/**
 * Simple perspective option for components that need just systemName and displayName
 * (e.g., CheckInScheduleEditorMolecule)
 */
export interface SimplePerspectiveOption {
  systemName: string;
  displayName: string;
}

/**
 * Build perspective options from a single schema definition
 *
 * This is useful for components that need perspectives from just one schema,
 * such as the CheckInScheduleEditorMolecule which needs perspectives from
 * the current sphere's schema.
 *
 * @param schema - The schema to extract perspectives from
 * @param t - Translation function for default perspective labels
 * @param includeDefaultPerspectives - Whether to include center_parent, center_responsible, center_center
 */
export function buildSchemaPerspectiveOptions(
  schema: SchemaForFilters | null | undefined,
  t: (key: string, options?: { defaultValue: string }) => string,
  includeDefaultPerspectives = true
): SimplePerspectiveOption[] {
  const perspectivesMap = new Map<string, SimplePerspectiveOption>();

  // Add default Center perspectives first
  if (includeDefaultPerspectives) {
    perspectivesMap.set('center_parent', {
      systemName: 'center_parent',
      displayName: t('filters.perspectives.centerParent', { defaultValue: 'Parent (Children)' }),
    });
    perspectivesMap.set('center_responsible', {
      systemName: 'center_responsible',
      displayName: t('filters.perspectives.centerResponsible', { defaultValue: 'Responsible' }),
    });
    perspectivesMap.set('center_center', {
      systemName: 'center_center',
      displayName: t('filters.perspectives.centerCenter', { defaultValue: 'Center' }),
    });
  }

  if (!schema) {
    return Array.from(perspectivesMap.values());
  }

  // Extract perspectives from the schema
  // First check top-level perspectives
  // Filter out hidden perspectives (is_hidden === true)
  if (schema.perspectives && Array.isArray(schema.perspectives)) {
    schema.perspectives.forEach((perspective: any) => {
      const systemName = perspective.system_name || perspective.systemName;
      if (!systemName) return;
      if (perspectivesMap.has(systemName)) return;
      // Skip hidden perspectives
      if (perspective.is_hidden) return;

      perspectivesMap.set(systemName, {
        systemName,
        displayName: perspective.name || perspective.label || systemName,
      });
    });
  }

  // Also check definition.perspectives for backwards compatibility
  const defPerspectives = schema.definition?.perspectives || [];
  defPerspectives.forEach((perspective: any) => {
    if (!perspective.system_name) return;
    if (perspectivesMap.has(perspective.system_name)) return;
    // Skip hidden perspectives
    if (perspective.is_hidden) return;

    perspectivesMap.set(perspective.system_name, {
      systemName: perspective.system_name,
      displayName: perspective.label || perspective.system_name,
    });
  });

  // Sort perspectives alphabetically by display name
  return Array.from(perspectivesMap.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );
}

/** Minimal level shape `resolveLevelEligibleSchemas` needs — a structural subset of `ContextFlowLevel`. */
export type LevelForScoping = Pick<ContextFlowLevel, 'id' | 'types' | 'filters' | 'direction'>;

/**
 * Resolve the concrete schema set a context level's (or its segments') property
 * catalog should be scoped to. Never returns an empty catalog — falls back to
 * `allSchemas` when no scope is inferable.
 *
 * Precedence:
 *  1. `level.types` if populated — a declared scope (the per-level `types` *editing
 *     control* is out of scope for this spec, but a value already on the level is honored).
 *  2. An explicit `type`/`label` filter on the level's own `filters.propertyFilters`.
 *  3. Hierarchy-reachable — schemas reachable from the previous level's (or, for the
 *     first level, the context's `applicability`) resolved schema set via
 *     `SchemaForFilters.children[]` (outwards) / `.parents[]` (inwards). This is a
 *     scoping *hint*, not a precise sphere-graph traversal result — see the spec's
 *     Open Questions for the future live-sample precision pass.
 *  4. All schemas (unscoped) when nothing above resolves.
 */
export function resolveLevelEligibleSchemas(
  level: LevelForScoping,
  allLevels: LevelForScoping[],
  contextApplicability: UnifiedFilters | undefined,
  allSchemas: SchemaForFilters[]
): SchemaForFilters[] {
  const scoped = resolveScopedSchemas(level, allLevels, contextApplicability, allSchemas);
  return scoped.length > 0 ? scoped : allSchemas;
}

/** Same precedence as `resolveLevelEligibleSchemas`, minus the final all-schemas fallback. */
function resolveScopedSchemas(
  level: LevelForScoping,
  allLevels: LevelForScoping[],
  contextApplicability: UnifiedFilters | undefined,
  allSchemas: SchemaForFilters[]
): SchemaForFilters[] {
  if (level.types && level.types.length > 0) {
    const declared = inferEligibleSchemas(
      [{ property: 'type', operator: 'in', values: level.types }],
      allSchemas
    );
    if (declared.length > 0) return declared;
  }

  // Walk the whole filter, groups included — the advanced editor folds every
  // top-level channel into `filterGroups`, so a type/family filter authored
  // there is invisible to a `propertyFilters`-only read and the level would
  // silently keep its pre-edit catalog scope.
  const explicit = inferEligibleSchemas(
    collectFilterScopeLeaves(level.filters),
    allSchemas
  );
  if (explicit.length > 0) return explicit;

  const idx = allLevels.findIndex((l) => l.id === level.id);
  const parentSchemas =
    idx > 0
      ? resolveScopedSchemas(allLevels[idx - 1], allLevels, contextApplicability, allSchemas)
      : inferEligibleSchemas(collectFilterScopeLeaves(contextApplicability), allSchemas);

  if (parentSchemas.length === 0) return [];

  const byName = new Map(allSchemas.map((s) => [s.system_name, s]));
  const reachable = new Map<string, SchemaForFilters>();
  parentSchemas.forEach((schema) => {
    const reachableNames = level.direction === 'inwards' ? schema.parents ?? [] : schema.children ?? [];
    reachableNames.forEach((name) => {
      const candidate = byName.get(name);
      if (candidate && !candidate.is_abstract && !candidate.is_hidden) {
        reachable.set(candidate.system_name, candidate);
      }
    });
  });
  return Array.from(reachable.values());
}
